import { useMutation } from '@tanstack/react-query';
import { aiSearchInput } from '@brechoflow/contracts';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { errorMessage } from '@/lib/api';
import { aiApi, isAiUnavailable } from './api';
import { Disclaimers, Notice, SEARCH_NOTICE, textareaClasses } from './notices';

const NOTICE_ID = 'ai-search-notice';

export function SearchPanel({ onUnavailable }: { onUnavailable: () => void }) {
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: aiApi.search,
    onError: (e) => (isAiUnavailable(e) ? onUnavailable() : setError(errorMessage(e))),
  });

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    const parsed = aiSearchInput.safeParse({ question });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Escreva a pergunta');
    run.mutate(parsed.data);
  };

  return (
    <div className="flex flex-col gap-5">
      <Notice id={NOTICE_ID} tone="warning">
        <strong className="font-semibold">{SEARCH_NOTICE}</strong>
      </Notice>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <FormField label="Sua pergunta" error={error ?? undefined} hint="Exemplo: quantos clientes novos cadastramos este mês?">
          {(a) => (
            <textarea
              {...a}
              aria-describedby={[NOTICE_ID, a['aria-describedby']].filter(Boolean).join(' ')}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              className={textareaClasses}
            />
          )}
        </FormField>
        <Button type="submit" size="lg" className="w-full sm:w-auto sm:self-start" loading={run.isPending}>
          Perguntar
        </Button>
      </form>

      {run.data && (
        <section aria-label="Resposta" className="flex flex-col gap-4">
          <p className="whitespace-pre-wrap rounded-panel border bg-card p-4 text-sm leading-relaxed">{run.data.answer}</p>
          <Disclaimers items={run.data.disclaimers} />
        </section>
      )}
    </div>
  );
}
